/**
 * QuoteRowActions — Phase 2B Item H.
 *
 * Menu contextuel "…" sur chaque ligne de la liste `/quotes`. 6 actions
 * avec matrice de visibilité selon le statut du devis (grisées si non
 * applicables, jamais cachées — cohérence UX).
 *
 * Actions :
 *   1. Dupliquer  (tous statuts) — dialog contact cible + recalcul TVA backend
 *   2. Accepter   (envoyé)       — accept-manual (client signe hors portail)
 *   3. Refuser    (envoyé)       — dialog LOSS_REASONS + detail obligatoire si "Autre"
 *   4. Envoyer    (brouillon+envoyé+expiré) — POST /quotes/{id}/send
 *   5. PDF        (tous)         — download GET /quotes/{id}/pdf
 *   6. Historique (tous)         — side-panel timeline
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MoreHorizontal, Copy, CheckCircle2, XCircle, Send, Mail, History } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useSignedPdfHref } from "@/hooks/useSignedPdfHref";
import { ViewPdfLink } from "@/components/ViewPdfLink";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import QuoteDuplicateDialog from "@/components/quote/QuoteDuplicateDialog";
import QuoteRefuseDialog from "@/components/quote/QuoteRefuseDialog";
import QuoteAcceptDialog from "@/components/quote/QuoteAcceptDialog";
import QuoteTimelinePanel from "@/components/quote/QuoteTimelinePanel";

// Phase 2C.M+ (2026-09-06) — Matrice statut × action refactorée.
// Correction bug UX P0 : `send` était TRUE pour envoye/expire → menu appelait
// /send qui retourne 400 (statut invalide). Solution : `send` désormais
// TRUE uniquement pour brouillon, `resend` distinct pour envoye/signe/expire.
// Chaque action grisée expose un `tip` (tooltip explicative HTML title).
const VISIBILITY = {
  brouillon: {
    duplicate: { on: true },
    accept:    { on: false, tip: "Non disponible : le devis est encore un brouillon (à envoyer d'abord)." },
    refuse:    { on: false, tip: "Non disponible : le devis n'a pas encore été envoyé." },
    send:      { on: true },
    resend:    { on: false, hide: true }, // masquer (pas de sens sur brouillon)
    pdf:       { on: true },
    timeline:  { on: true },
  },
  envoye: {
    duplicate: { on: true },
    accept:    { on: true },
    refuse:    { on: true },
    send:      { on: false, hide: true }, // masquer (utiliser resend)
    resend:    { on: true },
    pdf:       { on: true },
    timeline:  { on: true },
  },
  signe: {
    duplicate: { on: true },
    accept:    { on: false, tip: "Non disponible : le devis est déjà signé." },
    refuse:    { on: false, tip: "Non disponible : le devis est déjà signé." },
    send:      { on: false, hide: true },
    resend:    { on: true },
    pdf:       { on: true },
    timeline:  { on: true },
  },
  refuse: {
    duplicate: { on: true },
    accept:    { on: false, hide: true },
    refuse:    { on: false, hide: true },
    send:      { on: false, hide: true },
    resend:    { on: false, tip: "Non disponible : le devis a été refusé — dupliquez-le pour repartir sur une nouvelle offre." },
    pdf:       { on: true },
    timeline:  { on: true },
  },
  expire: {
    duplicate: { on: true },
    accept:    { on: false, tip: "Non disponible : le devis est expiré." },
    refuse:    { on: false, hide: true },
    send:      { on: false, hide: true },
    resend:    { on: true, tip: "Devis expiré : le client verra la date d'expiration." },
    pdf:       { on: true },
    timeline:  { on: true },
  },
};

export default function QuoteRowActions({ quote, onChanged }) {
  const navigate = useNavigate();
  const vis = VISIBILITY[quote.status] || VISIBILITY.brouillon;
  const [dlgDuplicate, setDlgDuplicate] = useState(false);
  const [dlgRefuse, setDlgRefuse] = useState(false);
  const [dlgAccept, setDlgAccept] = useState(false);
  const [dlgResend, setDlgResend] = useState(false);
  const [resendEmail, setResendEmail] = useState("");
  const [resendBusy, setResendBusy] = useState(false);
  const [panelTimeline, setPanelTimeline] = useState(false);
  // BLOC 2026-10-18 20h55 — pré-fetch href PDF à l'ouverture du menu (lazy).
  const [menuOpen, setMenuOpen] = useState(false);

  const openResendDialog = () => {
    // Pré-remplit avec l'email du contact (mais éditable ponctuellement)
    setResendEmail(quote.client_email || "");
    setDlgResend(true);
  };

  const submitResend = async () => {
    setResendBusy(true);
    try {
      const body = resendEmail && resendEmail !== quote.client_email
        ? { email_override: resendEmail.trim() }
        : {};
      const { data } = await api.post(`/quotes/${quote.id}/resend`, body);
      toast.success(`Devis renvoyé à ${data.sent_to}`);
      setDlgResend(false);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResendBusy(false);
    }
  };

  const handleSend = async () => {
    try {
      await api.post(`/quotes/${quote.id}/send`);
      toast.success("Devis envoyé par email au client");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-slate-400 hover:bg-slate-800/60 hover:text-slate-100"
            onClick={(e) => e.stopPropagation()}
            data-testid={`quote-row-menu-trigger-${quote.id}`}
            aria-label="Actions sur ce devis"
          >
            <MoreHorizontal className="h-5 w-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-56"
          onClick={(e) => e.stopPropagation()}
          data-testid={`quote-row-menu-${quote.id}`}
        >
          <DropdownMenuItem
            onClick={() => setDlgDuplicate(true)}
            disabled={!vis.duplicate.on}
            data-testid={`quote-action-duplicate-${quote.id}`}
            title={vis.duplicate.on ? undefined : vis.duplicate.tip}
          >
            <Copy className="mr-2 h-4 w-4" /> Dupliquer
          </DropdownMenuItem>
          {!vis.accept.hide && (
            <DropdownMenuItem
              onClick={() => setDlgAccept(true)}
              disabled={!vis.accept.on}
              data-testid={`quote-action-accept-${quote.id}`}
              title={vis.accept.on ? undefined : vis.accept.tip}
            >
              <CheckCircle2 className="mr-2 h-4 w-4" /> Accepter
            </DropdownMenuItem>
          )}
          {!vis.refuse.hide && (
            <DropdownMenuItem
              onClick={() => setDlgRefuse(true)}
              disabled={!vis.refuse.on}
              data-testid={`quote-action-refuse-${quote.id}`}
              title={vis.refuse.on ? undefined : vis.refuse.tip}
            >
              <XCircle className="mr-2 h-4 w-4" /> Refuser avec motif
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          {!vis.send.hide && (
            <DropdownMenuItem
              onClick={handleSend}
              disabled={!vis.send.on}
              data-testid={`quote-action-send-${quote.id}`}
              title={vis.send.on ? undefined : vis.send.tip}
            >
              <Send className="mr-2 h-4 w-4" /> Envoyer par email
            </DropdownMenuItem>
          )}
          {!vis.resend.hide && (
            <DropdownMenuItem
              onClick={openResendDialog}
              disabled={!vis.resend.on}
              data-testid={`quote-action-resend-${quote.id}`}
              title={vis.resend.on ? undefined : vis.resend.tip}
            >
              <Mail className="mr-2 h-4 w-4" /> Renvoyer par email
            </DropdownMenuItem>
          )}
          <DropdownMenuItem asChild disabled={!vis.pdf.on}>
            <ViewPdfLink
              entityType="quote"
              entityId={quote.id}
              enabled={menuOpen && vis.pdf.on}
              label="Exporter en PDF"
              testid={`quote-action-pdf-${quote.id}`}
              as="dropdown-item"
              disabled={!vis.pdf.on}
              title={vis.pdf.on ? undefined : vis.pdf.tip}
            />
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => setPanelTimeline(true)}
            disabled={!vis.timeline.on}
            data-testid={`quote-action-timeline-${quote.id}`}
          >
            <History className="mr-2 h-4 w-4" /> Voir l&apos;historique
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <QuoteDuplicateDialog
        open={dlgDuplicate}
        onOpenChange={setDlgDuplicate}
        quote={quote}
        onSuccess={(newId) => navigate(`/quotes/${newId}`)}
      />
      <QuoteRefuseDialog
        open={dlgRefuse}
        onOpenChange={setDlgRefuse}
        quote={quote}
        onSuccess={onChanged}
      />
      <QuoteAcceptDialog
        open={dlgAccept}
        onOpenChange={setDlgAccept}
        quote={quote}
        onSuccess={onChanged}
      />
      <QuoteTimelinePanel
        open={panelTimeline}
        onOpenChange={setPanelTimeline}
        quote={quote}
      />
      {/* Phase 2C.M+ — Dialog renvoi devis (email éditable ponctuellement) */}
      <Dialog open={dlgResend} onOpenChange={setDlgResend}>
        <DialogContent
          className="border-slate-800 bg-slate-900 text-slate-100 sm:max-w-md"
          data-testid={`quote-resend-dialog-${quote.id}`}
        >
          <DialogHeader>
            <DialogTitle>Renvoyer par email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-slate-400">
                Devis <strong className="text-slate-200">{quote.number || "brouillon"}</strong> —
                {" "}renvoi du PDF sans nouvelle version.
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Le numéro et le lien de signature déjà envoyé restent inchangés.
              </p>
            </div>
            <div>
              <Label htmlFor="resend-email">Adresse email destinataire</Label>
              <Input
                id="resend-email"
                type="email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                placeholder="client@exemple.be"
                className="mt-1 border-slate-700 bg-slate-950/50"
                data-testid={`quote-resend-email-input-${quote.id}`}
              />
              <p className="mt-1 text-xs text-slate-500">
                Modifier cette adresse ne change pas la fiche contact — c'est un renvoi ponctuel.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDlgResend(false)}
              className="text-slate-300"
              disabled={resendBusy}
            >
              Annuler
            </Button>
            <Button
              onClick={submitResend}
              disabled={resendBusy || !resendEmail || !resendEmail.includes("@")}
              className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
              data-testid={`quote-resend-submit-${quote.id}`}
            >
              {resendBusy ? "Envoi…" : "Renvoyer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
